/**
 * EnergyBreakdown — Collapsible energy tariff breakdown for quote cards.
 * Matches web frontend `EnergyBreakdown.tsx` logic.
 */
import { useState } from 'react'
import { View, Text, Pressable, StyleSheet } from 'react-native'
import { ChevronDown } from 'lucide-react-native'
import { colors, spacing } from '../theme'

// ─── Types ───────────────────────────────────────

export interface TariffData {
  spot_price_ore: number
  nettarif_ore: number
  nettarif_detail: {
    lavlast_ore: number
    hoejlast_ore: number
    spidslast_ore: number
    abonnement_kr_md: number
  }
  systemtarif_ore: number
  transmissionstarif_ore: number
  elafgift_ore: number
  dso_name: string
  price_area: string
  updated_at: string
  source: string
}

export const DEFAULT_KWH_YEAR = 4000

// ─── Utility functions ───────────────────────────

export const getEnergyBreakdown = (
  quote: { details?: Record<string, any>; monthly_price?: number },
  tariffs: TariffData | null,
  yearlyKwh: number = DEFAULT_KWH_YEAR,
): Record<string, number> | undefined => {
  if (quote.details?.energy_breakdown) return quote.details.energy_breakdown as Record<string, number>
  if (!tariffs) return undefined
  const sub = Number(quote.details?.subscription ?? quote.details?.monthly_subscription ?? 0)
  const surchargeOre = Number(quote.details?.surcharge_ore ?? quote.details?.kwh_surcharge ?? 0)
  if (sub === 0 && surchargeOre === 0) return undefined

  const monthlyKwh = yearlyKwh / 12
  return {
    subscription: sub,
    kwh_cost: (surchargeOre / 100) * monthlyKwh,
    spot_price: (tariffs.spot_price_ore / 100) * monthlyKwh,
    nettarif: (tariffs.nettarif_ore / 100) * monthlyKwh,
    nettarif_abonnement: tariffs.nettarif_detail.abonnement_kr_md,
    elafgift: (tariffs.elafgift_ore / 100) * monthlyKwh,
    systemtarif: (tariffs.systemtarif_ore / 100) * monthlyKwh,
    transmissionstarif: (tariffs.transmissionstarif_ore / 100) * monthlyKwh,
  }
}

/** Compute the total monthly energy price from a breakdown + current tariffs.
 *  Each component is rounded individually to match the breakdown line items shown in the UI. */
export const calcEnergyTotal = (breakdown: Record<string, number>, tariffs: TariffData, yearlyKwh: number): number => {
  const safeYearlyKwh = Math.max(yearlyKwh || DEFAULT_KWH_YEAR, 1)
  const mKwh = Math.round(safeYearlyKwh / 12)
  const sub = Math.round(breakdown.subscription || 0)
  const surchargeOre = breakdown.kwh_cost != null ? (breakdown.kwh_cost / (safeYearlyKwh / 12)) * 100 : 0
  const surchargeKr = Math.round((surchargeOre / 100) * mKwh)
  const spotKr = Math.round((tariffs.spot_price_ore / 100) * mKwh)
  const netAbonKr = Math.round(breakdown.nettarif_abonnement || 0)
  const netTarifKr = Math.round((tariffs.nettarif_ore / 100) * mKwh)
  const elafgiftKr = Math.round((tariffs.elafgift_ore / 100) * mKwh)
  const systemtarifKr = Math.round((tariffs.systemtarif_ore / 100) * mKwh)
  const transmissionstarifKr = Math.round((tariffs.transmissionstarif_ore / 100) * mKwh)
  return sub + surchargeKr + spotKr + netAbonKr + netTarifKr + elafgiftKr + systemtarifKr + transmissionstarifKr
}

// ─── Component ───────────────────────────────────

interface Props {
  breakdown: Record<string, number>
  tariffs: TariffData | null
  details: Record<string, any>
  yearlyKwh?: number
  compact?: boolean
}

export default function EnergyBreakdownAccordion({ breakdown, tariffs, details, yearlyKwh: yearlyKwhProp, compact = false }: Props) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const toggle = (section: string) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  const yearlyKwh = Number(details?.yearly_kwh) || yearlyKwhProp || DEFAULT_KWH_YEAR
  const mKwh = Math.round(yearlyKwh / 12)
  const surchargeOre = Number(details?.kwh_surcharge ?? details?.surcharge_ore ?? 0)
  const subscriptionKr = Math.round(breakdown.subscription ?? 0)
  const surchargeKr = Math.round((surchargeOre / 100) * mKwh)

  const spotOre = tariffs ? tariffs.spot_price_ore : 0
  const spotKr = Math.round((spotOre / 100) * mKwh)

  const netTarifOre = tariffs ? tariffs.nettarif_ore : 0
  const netTarifKr = Math.round((netTarifOre / 100) * mKwh)
  const netAbonKr = tariffs ? Math.round(tariffs.nettarif_detail.abonnement_kr_md) : Math.round(breakdown.nettarif_abonnement ?? 0)
  const netTotal = netTarifKr + netAbonKr

  const elafgiftOre = tariffs ? tariffs.elafgift_ore : 0
  const systemtarifOre = tariffs ? tariffs.systemtarif_ore : 0
  const transmissionstarifOre = tariffs ? tariffs.transmissionstarif_ore : 0
  const elafgiftKr = Math.round((elafgiftOre / 100) * mKwh)
  const systemtarifKr = Math.round((systemtarifOre / 100) * mKwh)
  const transmissionstarifKr = Math.round((transmissionstarifOre / 100) * mKwh)
  const afgifterTotal = elafgiftKr + systemtarifKr + transmissionstarifKr

  const fasteKr = spotKr + netTotal + afgifterTotal
  const leverandorTotal = subscriptionKr + surchargeKr
  // Use calcEnergyTotal for the displayed total to match the header price exactly
  const calculatedTotal = tariffs ? calcEnergyTotal(breakdown, tariffs, yearlyKwh) : (leverandorTotal + fasteKr)

  const fmt1 = (ore: number) => (Math.round(ore * 10) / 10).toLocaleString('da-DK')

  const CalcLine = ({ label, orePerKwh, kr }: { label: string; orePerKwh: number; kr: number }) => (
    <View style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: 1 }}>
      <Text style={s.calcLabel}>{label}</Text>
      <Text style={s.calcFormula} numberOfLines={1}>
        {mKwh} kWh × {fmt1(orePerKwh)} øre =
      </Text>
      <Text style={s.calcKrFixed}>{kr} kr</Text>
    </View>
  )

  return (
    <View style={s.container}>
      <Text style={s.basis}>
        Baseret på {yearlyKwh.toLocaleString('da-DK')} kWh/år ({mKwh} kWh/md)
      </Text>

      {/* GROUP 1: Leverandørens tillæg */}
      <View style={s.group}>
        <Text style={s.groupTitle}>LEVERANDØRENS TILLÆG</Text>
        <Text style={s.groupSubtitle}>Ændres ved leverandørskift</Text>
        {subscriptionKr > 0 && (
          <View style={s.calcLine}>
            <Text style={s.calcLabel}>Abonnement</Text>
            <Text style={s.calcKr}>{subscriptionKr} kr</Text>
          </View>
        )}
        {surchargeOre > 0 && <CalcLine label="Tillæg pr. kWh" orePerKwh={surchargeOre} kr={surchargeKr} />}
        {subscriptionKr === 0 && surchargeOre === 0 && (
          <Text style={s.noSurcharge}>Kun spotpris — ingen leverandørtillæg</Text>
        )}
      </View>

      {/* GROUP 2: Faste omkostninger */}
      <View style={s.group}>
        <Text style={s.groupTitleMuted}>FASTE OMKOSTNINGER</Text>
        <Text style={s.groupSubtitleMuted}>Ens for alle udbydere · aktuelle markedspriser</Text>

        <CalcLine label="Spotpris (3-mdr. gns.)" orePerKwh={spotOre} kr={spotKr} />

        {/* Netselskab */}
        <Pressable onPress={() => toggle('net')} style={s.accordionRow}>
          <View style={s.accordionLabel}>
            <Text style={s.calcLabel}>Netselskab</Text>
            <ChevronDown size={12} color="#94a3b8" style={openSections.has('net') ? { transform: [{ rotate: '180deg' }] } : undefined} />
          </View>
          <Text style={s.calcKrFixed}>{netTotal} kr</Text>
        </Pressable>
        {openSections.has('net') && tariffs && (
          <View style={s.subGroup}>
            <View style={s.calcLine}>
              <Text style={s.subLabel}>Netabonnement (fast)</Text>
              <Text style={s.subValue}>{netAbonKr} kr</Text>
            </View>
            <View style={s.calcLine}>
              <Text style={s.subLabel}>Nettarif (vægtet gns.)</Text>
              <Text style={s.subValue}>{netTarifKr} kr</Text>
            </View>
            <View style={s.tariffDetail}>
              <View style={s.calcLine}>
                <Text style={s.tariffLabel}>Lavlast (00–06)</Text>
                <Text style={s.tariffValue}>{Math.round(tariffs.nettarif_detail.lavlast_ore)} øre/kWh</Text>
              </View>
              <View style={s.calcLine}>
                <Text style={s.tariffLabel}>Højlast (06–17, 21–24)</Text>
                <Text style={s.tariffValue}>{Math.round(tariffs.nettarif_detail.hoejlast_ore)} øre/kWh</Text>
              </View>
              <View style={s.calcLine}>
                <Text style={s.tariffLabel}>Spidslast (17–21)</Text>
                <Text style={s.tariffValue}>{Math.round(tariffs.nettarif_detail.spidslast_ore)} øre/kWh</Text>
              </View>
            </View>
          </View>
        )}

        {/* Afgifter */}
        <Pressable onPress={() => toggle('afgifter')} style={s.accordionRow}>
          <View style={s.accordionLabel}>
            <Text style={s.calcLabel}>Afgifter</Text>
            <ChevronDown size={12} color="#94a3b8" style={openSections.has('afgifter') ? { transform: [{ rotate: '180deg' }] } : undefined} />
          </View>
          <Text style={s.calcKrFixed}>{afgifterTotal} kr</Text>
        </Pressable>
        {openSections.has('afgifter') && tariffs && (
          <View style={s.subGroup}>
            <CalcLine label="Elafgift" orePerKwh={elafgiftOre} kr={elafgiftKr} />
            <CalcLine label="Systemtarif" orePerKwh={systemtarifOre} kr={systemtarifKr} />
            <CalcLine label="Transmissionstarif" orePerKwh={transmissionstarifOre} kr={transmissionstarifKr} />
          </View>
        )}
      </View>

      {/* TOTAL */}
      <View style={s.totalRow}>
        <Text style={s.totalLabel}>Estimeret månedspris</Text>
        <Text style={s.totalValue}>{calculatedTotal.toLocaleString('da-DK')} kr/md</Text>
      </View>
      <Text style={s.totalNote}>Inkl. moms · Faste omkostninger varierer med markedspriserne</Text>
    </View>
  )
}

const s = StyleSheet.create({
  container: { gap: 2 },
  basis: { fontSize: 11, color: colors.text.muted, marginBottom: 8 },
  group: { paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: colors.surface, marginBottom: 8 },
  groupTitle: { fontSize: 11, fontWeight: '600', color: colors.text.primary, letterSpacing: 0.5 },
  groupSubtitle: { fontSize: 10, color: colors.primary, marginBottom: 6, marginTop: 1 },
  groupTitleMuted: { fontSize: 11, fontWeight: '600', color: colors.text.secondary, letterSpacing: 0.5 },
  groupSubtitleMuted: { fontSize: 10, color: colors.text.muted, marginBottom: 6, marginTop: 1 },
  calcLine: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', paddingVertical: 1 },
  calcLabel: { fontSize: 12, color: colors.text.secondary, flexShrink: 0 },
  calcValue: { fontSize: 11, color: colors.text.muted, textAlign: 'right' },
  calcKr: { fontSize: 12, color: colors.text.secondary, fontWeight: '500' },
  calcFormula: { flex: 1, fontSize: 11, color: colors.text.muted, textAlign: 'right', paddingHorizontal: 4 },
  calcKrFixed: { fontSize: 12, color: colors.text.secondary, fontWeight: '500', minWidth: 52, textAlign: 'right' },
  noSurcharge: { fontSize: 11, color: colors.text.muted, fontStyle: 'italic' },
  accordionRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 3 },
  accordionLabel: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  subGroup: { marginLeft: 16, gap: 2, marginBottom: 4 },
  subLabel: { fontSize: 11, color: colors.text.muted },
  subValue: { fontSize: 11, color: colors.text.secondary, fontWeight: '500' },
  tariffDetail: { marginTop: 4, paddingTop: 4, borderTopWidth: 1, borderTopColor: colors.surface, gap: 2 },
  tariffLabel: { fontSize: 11, color: colors.text.muted },
  tariffValue: { fontSize: 11, color: colors.text.muted },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 13, fontWeight: '600', color: colors.text.primary },
  totalValue: { fontSize: 13, fontWeight: '600', color: colors.text.primary, minWidth: 52, textAlign: 'right' as const },
  totalNote: { fontSize: 11, color: colors.text.muted, marginTop: 2 },
})
